import type { CSSProperties } from 'react';

/**
 * The PXC icon set (§28).
 *
 * One 24×24 grid, one 1.6px stroke, round caps and joins, no fills. Every glyph
 * is authored here so the whole set shares a visual language — and so emoji are
 * never used as icons anywhere in the product.
 *
 * A `h0` segment with a round cap renders a dot, which is how the few dotted
 * glyphs (info, alert, wallet clasp) are drawn without a second element type.
 */

const PATHS = {
  home: ['M4 10.6 12 4l8 6.6V19a2 2 0 0 1-2 2h-3.5v-6h-5v6H6a2 2 0 0 1-2-2z'],
  pickaxe: ['M4 20l9.2-9.2', 'M8.6 7.4a8.5 8.5 0 0 1 11.4 3.4', 'M8.6 7.4a8.5 8.5 0 0 0 3.4 11.4'],
  tasks: [
    'M10 6h10',
    'M10 12h10',
    'M10 18h10',
    'm3 6 1.6 1.6L7.6 4.4',
    'm3 12 1.6 1.6L7.6 10.4',
    'm3 18 1.6 1.6L7.6 16.4',
  ],
  referral: [
    'M9.5 10.5a3.25 3.25 0 1 0 0-6.5 3.25 3.25 0 0 0 0 6.5',
    'M3 20v-1.2A4.3 4.3 0 0 1 7.3 14.5h4.4A4.3 4.3 0 0 1 16 18.8V20',
    'M16.5 4.6a3.25 3.25 0 0 1 0 5.8',
    'M18 14.8A4.3 4.3 0 0 1 21 18.9V20',
  ],
  wallet: [
    'M4 7.6A2.6 2.6 0 0 1 6.6 5H17a1 1 0 0 1 1 1v2.4',
    'M4 7.6v9.8A2.6 2.6 0 0 0 6.6 20H19a1 1 0 0 0 1-1v-8.4a1 1 0 0 0-1-1H6.6A2.6 2.6 0 0 1 4 7.6Z',
    'M16.4 14.8h0',
  ],
  user: [
    'M12 12.2a4.1 4.1 0 1 0 0-8.2 4.1 4.1 0 0 0 0 8.2',
    'M20 21v-1.3a5 5 0 0 0-5-5H9a5 5 0 0 0-5 5V21',
  ],
  chevronRight: ['m9.5 5 7 7-7 7'],
  /*
   * Three glyphs the Invite screen needs by name rather than by approximation:
   * a crew, a confirmed friend, and a reward still to come. Drawn here so they
   * share the set's grid and stroke instead of arriving as raster art that
   * cannot be scaled.
   */
  pickaxeCrossed: [
    'M4.9 19.3 16 8.2',
    'M19.1 19.3 8 8.2',
    'M13.3 5.4a7 7 0 0 1 5.3 5.3',
    'M10.7 5.4a7 7 0 0 0-5.3 5.3',
  ],
  checkCircle: [
    'M12 3.4a8.6 8.6 0 1 0 0 17.2 8.6 8.6 0 0 0 0-17.2',
    'm8.1 12.1 2.7 2.7 5.1-5.3',
  ],
  gift: [
    'M3.7 7.2h16.6v3.6H3.7z',
    'M5.3 10.8h13.4v8.6a1.6 1.6 0 0 1-1.6 1.6H6.9a1.6 1.6 0 0 1-1.6-1.6z',
    'M12 7.2V21',
    'M12 7.2S10.9 3.1 8.9 3.1a2.05 2.05 0 0 0 0 4.1',
    'M12 7.2s1.1-4.1 3.1-4.1a2.05 2.05 0 0 1 0 4.1',
  ],

  chevronLeft: ['m14.5 5-7 7 7 7'],
  chevronDown: ['m5 9.5 7 7 7-7'],
  close: ['M6.4 6.4 17.6 17.6', 'M17.6 6.4 6.4 17.6'],
  check: ['m4.8 12.4 5 5 9.4-10.4'],
  clock: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18', 'M12 7.4V12l3.2 2'],
  play: ['M8.4 5.6v12.8L19 12z'],
  lock: [
    'M6 10.6h12a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-8.4a1 1 0 0 1 1-1Z',
    'M8.6 10.6V7.6a3.4 3.4 0 1 1 6.8 0v3',
  ],
  bolt: ['M13.6 3 5.4 13.4h5.6L10.4 21l8.2-10.4H13z'],
  coin: [
    'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18',
    'M12 7v10',
    'M14.6 9.4a2.6 2.6 0 0 0-5 .8c0 2.6 5 1.4 5 4a2.6 2.6 0 0 1-5 .6',
  ],
  gem: ['m12 21 8-11-3.6-6H7.6L4 10z', 'M4 10h16', 'M8.4 4 12 21 15.6 4'],
  arrowUpRight: ['M7 17 17 7', 'M9.4 7H17v7.6'],
  arrowDownLeft: ['M17 7 7 17', 'M14.6 17H7V9.4'],
  copy: [
    'M9.5 8.5h8.6a1.9 1.9 0 0 1 1.9 1.9v8.6a1.9 1.9 0 0 1-1.9 1.9H9.5a1.9 1.9 0 0 1-1.9-1.9v-8.6a1.9 1.9 0 0 1 1.9-1.9Z',
    'M15.4 6.2V4.9A1.9 1.9 0 0 0 13.5 3H4.9A1.9 1.9 0 0 0 3 4.9v8.6a1.9 1.9 0 0 0 1.9 1.9h1.3',
  ],
  share: ['M12 15.4V4', 'm8.4 7.6 3.6-3.6 3.6 3.6', 'M5 13v5.4A2.6 2.6 0 0 0 7.6 21h8.8A2.6 2.6 0 0 0 19 18.4V13'],
  settings: [
    'M3 8h9',
    'M16.4 8H21',
    'M3 16h4.6',
    'M12 16h9',
    'M14.2 5.8a2.2 2.2 0 1 0 0 4.4 2.2 2.2 0 0 0 0-4.4',
    'M9.8 13.8a2.2 2.2 0 1 0 0 4.4 2.2 2.2 0 0 0 0-4.4',
  ],
  bell: ['M18 9.5a6 6 0 1 0-12 0c0 4.8-2 6.1-2 6.1h16s-2-1.3-2-6.1', 'M13.8 19.5a2.1 2.1 0 0 1-3.6 0'],
  shield: ['M12 3.2 20 6.1v5.8c0 4.5-3.2 7.7-8 9.1-4.8-1.4-8-4.6-8-9.1V6.1z', 'm9 12 2.2 2.2L15.4 10'],
  help: [
    'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18',
    'M9.5 9.6a2.6 2.6 0 0 1 5.1.6c0 1.9-2.6 2.2-2.6 3.9',
    'M12 17.4h0',
  ],
  external: ['M14.4 4H20v5.6', 'M20 4l-8.4 8.4', 'M18 13.6v4.8A2.6 2.6 0 0 1 15.4 21H6.6A2.6 2.6 0 0 1 4 18.4V9.6A2.6 2.6 0 0 1 6.6 7h4.8'],
  filter: ['M4 6.5h16', 'M7 12h10', 'M10 17.5h4'],
  refresh: ['M20.4 12a8.4 8.4 0 1 1-2.5-6', 'M20.4 3.6v4.8h-4.8'],
  alert: ['M12 3.2 2.6 20h18.8z', 'M12 9.4v4.4', 'M12 16.8h0'],
  info: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18', 'M12 11.2v5.4', 'M12 7.6h0'],
  plus: ['M12 5.5v13', 'M5.5 12h13'],
  minus: ['M5.5 12h13'],
  spinner: ['M12 3.4a8.6 8.6 0 0 1 8.6 8.6'],
  sparkle: [
    'm11 3 1.7 4.9L17.6 9.6l-4.9 1.7L11 16.2 9.3 11.3 4.4 9.6l4.9-1.7z',
    'm18.4 15.6.55 1.55 1.55.55-1.55.55-.55 1.55-.55-1.55L16.3 17.7l1.55-.55z',
  ],
  trendUp: ['m4 16.4 5.2-5.2 3.4 3.4L20 7.2', 'M15 7.2h5v5'],
  arrowRight: ['M4 12h15', 'm13 6 6 6-6 6'],
} as const;

export type IconName = keyof typeof PATHS;

export interface IconProps {
  name: IconName;
  size?: number;
  /** Stroke width in the 24-unit grid; the set default is 1.6. */
  strokeWidth?: number;
  className?: string;
  style?: CSSProperties;
  /** Supply when the icon is the only content of a control. */
  title?: string;
}

export function Icon({ name, size = 20, strokeWidth = 1.6, className, style, title }: IconProps) {
  const paths = PATHS[name];
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      {paths.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

/** The spinner arc, rotating. Used by loading buttons and inline waits. */
export function Spinner({ size = 18, className }: { size?: number; className?: string }) {
  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        animation: 'pxc-spin 720ms linear infinite',
        transformOrigin: 'center',
      }}
    >
      <Icon name="spinner" size={size} strokeWidth={2} />
    </span>
  );
}
